import React from 'react';
import { Droplets, Dumbbell, Target, Settings } from 'lucide-react';

export type NavTab = 'hydration' | 'workout' | 'recomp' | 'settings';

interface BottomNavProps {
  currentTab: NavTab;
  onTabChange: (tab: NavTab) => void;
  isWorkoutActive?: boolean;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  currentTab,
  onTabChange,
  isWorkoutActive = false,
}) => {
  const tabs = [
    {
      id: 'hydration' as NavTab,
      label: 'Hidratação',
      icon: Droplets,
      activeColor: 'text-[#007AFF] dark:text-[#30D6FB]',
      activeBg: 'bg-[#007AFF]/12 dark:bg-[#30D6FB]/15 border-[#007AFF]/25 dark:border-[#30D6FB]/30',
      indicator: 'bg-[#007AFF] dark:bg-[#30D6FB]',
    },
    {
      id: 'workout' as NavTab,
      label: isWorkoutActive ? 'Treino 🔥' : 'Treino',
      icon: Dumbbell,
      activeColor: 'text-[#30D158]',
      activeBg: 'bg-[#30D158]/15 border-[#30D158]/30',
      indicator: 'bg-[#30D158]',
      pulse: isWorkoutActive,
    },
    {
      id: 'recomp' as NavTab,
      label: 'Recomp',
      icon: Target,
      activeColor: 'text-[#AF52DE] dark:text-[#BF5AF2]',
      activeBg: 'bg-[#AF52DE]/15 dark:bg-[#BF5AF2]/15 border-[#AF52DE]/30 dark:border-[#BF5AF2]/30',
      indicator: 'bg-[#AF52DE] dark:bg-[#BF5AF2]',
    },
    {
      id: 'settings' as NavTab,
      label: 'Ajustes',
      icon: Settings,
      activeColor: 'text-[#007AFF] dark:text-[#0A84FF]',
      activeBg: 'bg-[#007AFF]/12 dark:bg-[#0A84FF]/15 border-[#007AFF]/25 dark:border-[#0A84FF]/30',
      indicator: 'bg-[#007AFF] dark:bg-[#0A84FF]',
    },
  ];

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-40 px-4 max-w-xl mx-auto pointer-events-none pb-2"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 14px)' }}
    >
      {/* Floating Apple Liquid Glass Dock */}
      <nav className="pointer-events-auto liquid-glass-dock rounded-[36px] p-2 shadow-[0_16px_40px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.95)] transition-all duration-300">
        <div className="flex items-center justify-between gap-1.5">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => {
                  if ('vibrate' in navigator) navigator.vibrate(30);
                  onTabChange(tab.id);
                }}
                className={`flex-1 flex flex-col items-center justify-center py-2.5 px-1 rounded-[24px] transition-all duration-200 apple-press relative ${
                  isActive
                    ? `${tab.activeBg} ${tab.activeColor} shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]`
                    : 'text-slate-500 hover:text-slate-900 dark:text-white/40 dark:hover:text-white/80 hover:bg-black/[0.04] dark:hover:bg-white/[0.05]'
                }`}
              >
                <div className="relative">
                  <Icon
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isActive ? 'scale-110 stroke-[2.4] drop-shadow-[0_0_8px_currentColor]' : 'stroke-[1.8]'
                    }`}
                  />
                  {tab.pulse && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#30D158] animate-ping" />
                  )}
                </div>

                <span className={`text-[11px] font-bold tracking-tight mt-1 ${isActive ? tab.activeColor : 'text-slate-500 dark:text-white/45'}`}>
                  {tab.label}
                </span>

                {isActive && (
                  <div className={`absolute -bottom-0.5 w-5 h-0.5 rounded-full ${tab.indicator} shadow-[0_0_10px_currentColor]`} />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
